import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Box, Typography, Paper, LinearProgress, Skeleton, Table,
  TableHead, TableRow, TableCell, TableBody, Chip, Divider,
  ToggleButton, ToggleButtonGroup, Tooltip, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Switch,
  FormControlLabel, Alert, Stack,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import {
  getBilling,
  getInferenceBalance,
  fundInferenceBalance,
  updateInferenceAutoRecharge,
} from '../../api/developerApi';
import ApiErrorAlert from '../../components/ApiErrorAlert';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmt(n) { return (n ?? 0).toLocaleString(); }
function fmtBytes(n) {
  if (!n) return '0 B';
  if (n >= 1e9) return (n / 1e9).toFixed(2) + ' GB';
  if (n >= 1e6) return (n / 1e6).toFixed(2) + ' MB';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + ' KB';
  return n + ' B';
}

function SectionHeader({ title, tooltip }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
      <Typography variant="subtitle2" fontWeight={600}>{title}</Typography>
      {tooltip && (
        <Tooltip title={tooltip} placement="right">
          <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help' }} />
        </Tooltip>
      )}
    </Box>
  );
}

function StatRow({ label, count, bytes, credits, estimatedCredits, loading }) {
  if (loading) return (
    <TableRow>
      <TableCell><Skeleton /></TableCell>
      <TableCell><Skeleton /></TableCell>
      <TableCell><Skeleton /></TableCell>
    </TableRow>
  );
  const creditVal = credits ?? estimatedCredits;
  const isEstimate = estimatedCredits !== undefined;
  return (
    <TableRow hover>
      <TableCell sx={{ fontSize: 13 }}>{label}</TableCell>
      <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>
        {bytes !== undefined ? fmtBytes(bytes) : fmt(count)}
      </TableCell>
      <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>
        {creditVal > 0
          ? <Chip
              label={isEstimate ? `~${fmt(creditVal)} est.` : fmt(creditVal)}
              size="small"
              sx={{ bgcolor: '#EFF6FF', color: '#1D4ED8', fontWeight: 600, fontSize: 11 }}
            />
          : <Typography variant="caption" color="text.disabled">—</Typography>}
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// UsagePage
// ---------------------------------------------------------------------------

export default function UsagePage() {
  const [view, setView] = useState('total'); // 'total' | 'live' | 'test'

  const { data: billing, isLoading, error: billingError } = useQuery('billing', getBilling);

  const usage = billing?.usage;
  const period = usage?.period ?? '—';

  // Select the data slice based on view toggle
  const data = view === 'live'
    ? usage?.tenants?.live
    : view === 'test'
    ? usage?.tenants?.test
    : usage;

  const creditsUsed = data?.credits?.used ?? 0;
  const planLimit = billing?.planLimitCredits ?? null;
  const pct = planLimit ? Math.min(100, Math.round((creditsUsed / planLimit) * 100)) : 0;

  const breakdown = usage?.credits?.breakdown ?? {};

  const sq = data?.search?.queries ?? {};
  const si = data?.search?.ingest ?? {};
  const ss = data?.search?.storageBytes ?? {};
  const di = data?.documents?.ingest ?? {};
  const ds = data?.documents?.storageBytes ?? {};
  const dl = data?.documents?.downloads ?? {};
  const rw = data?.records?.writes ?? {};
  const rs = data?.records?.storageBytes ?? {};
  const rps = data?.records?.payloadStorageBytes ?? {};
  const ras = data?.records?.auditStorageBytes ?? {};

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Usage</Typography>
          <Typography variant="body2" color="text.secondary">
            Billing period: {isLoading ? <Skeleton component="span" width={60} /> : period}
          </Typography>
        </Box>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(_, v) => v && setView(v)}
          size="small"
        >
          <ToggleButton value="total">All envs</ToggleButton>
          <ToggleButton value="live">Live</ToggleButton>
          <ToggleButton value="test">Test</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <ApiErrorAlert error={billingError} sx={{ mb: 2 }} />

      {/* Credits summary */}
      <Paper elevation={0} sx={{ p: 3, border: '1px solid #E4E4E7', borderRadius: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>Credits used</Typography>
          {isLoading
            ? <Skeleton width={100} />
            : <Typography variant="subtitle2" fontWeight={600}>
                {fmt(creditsUsed)} / {planLimit ? fmt(planLimit) : '∞'}
              </Typography>}
        </Box>
        {isLoading
          ? <Skeleton height={8} sx={{ borderRadius: 1 }} />
          : planLimit
            ? <LinearProgress variant="determinate" value={pct} sx={{
                height: 8, borderRadius: 1, bgcolor: '#DBEAFE',
                '& .MuiLinearProgress-bar': {
                  bgcolor: pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#3B82F6',
                },
              }} />
            : <Typography variant="caption" color="text.secondary">Unlimited</Typography>}
        {!isLoading && planLimit && (
          <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
            {pct}% of plan limit used
          </Typography>
        )}

        {/* Credit breakdown chips — total view only */}
        {view === 'total' && !isLoading && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            {[
              { label: 'Search queries', value: breakdown.searchQueries },
              { label: 'Search ingest', value: breakdown.searchIngest },
              { label: 'Documents', value: breakdown.documents },
              { label: 'Records', value: breakdown.records },
              { label: 'Storage (est.)', value: breakdown.storageEstimate },
            ].map(({ label, value }) => value > 0 && (
              <Chip
                key={label}
                label={`${label}: ${fmt(value)}`}
                size="small"
                sx={{ bgcolor: '#F4F4F5', color: '#3F3F46', fontSize: 11 }}
              />
            ))}
          </Box>
        )}
      </Paper>

      {/* Search */}
      <Paper elevation={0} sx={{ border: '1px solid #E4E4E7', borderRadius: 2, mb: 3 }}>
        <Box sx={{ p: 2.5, pb: 1 }}>
          <SectionHeader title="Search" />
        </Box>
        <Table size="small">
          <TableHead sx={{ bgcolor: 'background.default' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Operation</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Volume</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Credits</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <StatRow label="Text queries"     count={sq.text?.count}     credits={sq.text?.credits}     loading={isLoading} />
            <StatRow label="Semantic queries" count={sq.semantic?.count} credits={sq.semantic?.credits} loading={isLoading} />
            <StatRow label="Hybrid queries"   count={sq.hybrid?.count}   credits={sq.hybrid?.credits}   loading={isLoading} />
            <StatRow label="Text ingest"      bytes={si.text?.bytes}     credits={si.text?.credits}     loading={isLoading} />
            <StatRow label="Semantic ingest"  bytes={si.semantic?.bytes} credits={si.semantic?.credits} loading={isLoading} />
            <StatRow label="Hybrid ingest"    bytes={si.hybrid?.bytes}   credits={si.hybrid?.credits}   loading={isLoading} />
            <StatRow label="Parent chunk ingest" count={si.parentChunks?.count} bytes={si.parentChunks?.bytes} credits={si.parentChunks?.credits} loading={isLoading} />
          </TableBody>
        </Table>
        <Divider />
        <Box sx={{ p: 2.5, pt: 1.5 }}>
          <SectionHeader title="Search storage" tooltip="Estimated monthly charge based on current stored bytes. Billed at end of billing cycle." />
        </Box>
        <Table size="small">
          <TableHead sx={{ bgcolor: 'background.default' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Index type</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Stored</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Est. credits</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <StatRow label="Text index"      bytes={ss.text?.bytes}         estimatedCredits={ss.text?.estimatedCredits}         loading={isLoading} />
            <StatRow label="Semantic index"  bytes={ss.semantic?.bytes}     estimatedCredits={ss.semantic?.estimatedCredits}     loading={isLoading} />
            <StatRow label="Hybrid index"    bytes={ss.hybrid?.bytes}       estimatedCredits={ss.hybrid?.estimatedCredits}       loading={isLoading} />
            <StatRow label="Parent chunks"   bytes={ss.parentChunks?.bytes} estimatedCredits={ss.parentChunks?.estimatedCredits} loading={isLoading} />
          </TableBody>
        </Table>
      </Paper>

      {/* Documents */}
      <Paper elevation={0} sx={{ border: '1px solid #E4E4E7', borderRadius: 2, mb: 3 }}>
        <Box sx={{ p: 2.5, pb: 1 }}>
          <SectionHeader title="Documents" />
        </Box>
        <Table size="small">
          <TableHead sx={{ bgcolor: 'background.default' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Operation</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Volume</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Credits</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <StatRow label="Text ingest"   count={di.text?.count} credits={di.text?.credits} loading={isLoading} />
            <StatRow label="File ingest"   count={di.file?.count} credits={di.file?.credits} loading={isLoading} />
            <StatRow label="Downloads"     bytes={dl.bytes}       credits={dl.credits}       loading={isLoading} />
            <StatRow label="Text storage"  bytes={ds.text?.bytes} estimatedCredits={ds.text?.estimatedCredits} loading={isLoading} />
            <StatRow label="File storage"  bytes={ds.file?.bytes} estimatedCredits={ds.file?.estimatedCredits} loading={isLoading} />
          </TableBody>
        </Table>
      </Paper>

      {/* Records */}
      <Paper elevation={0} sx={{ border: '1px solid #E4E4E7', borderRadius: 2 }}>
        <Box sx={{ p: 2.5, pb: 1 }}>
          <SectionHeader title="Records" />
        </Box>
        <Table size="small">
          <TableHead sx={{ bgcolor: 'background.default' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Operation</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Volume</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Credits</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <StatRow label="Writes"              count={rw.count}            credits={rw.credits}                    loading={isLoading} />
            <StatRow label="Lookup field writes" count={rw.lookupFieldCount} credits={null}                          loading={isLoading} />
            <StatRow label="Storage"             bytes={rs.bytes}            estimatedCredits={rs.estimatedCredits}  loading={isLoading} />
            <StatRow label="Payload storage (S3)" bytes={rps.bytes}          estimatedCredits={rps.estimatedCredits} loading={isLoading} />
            <StatRow label="Audit history storage" bytes={ras.bytes}         estimatedCredits={ras.estimatedCredits} loading={isLoading} />
          </TableBody>
        </Table>
      </Paper>

      {/* Inference balance — split-billing pre-paid ledger. Distinct from the
           monthly plan credit allowance shown in the Credits card above. */}
      <InferenceBalanceCard usage={usage} />
    </Box>
  );
}

// ===========================================================================
// InferenceBalanceCard
//
// Shows the partner's pre-paid inferenceBalanceCents balance plus the
// per-endpoint inference activity from the usage report. Two actions:
//   1. "Fund" — opens a dialog to top up via Stripe Checkout (paid plan only)
//   2. "Auto-recharge" — opens a settings dialog to enable/configure
//
// Both actions are gated on `canFund` returned by GET /developer/billing/inference
// (paid plan + Stripe customer on file). Free-tier partners see the seed balance
// from PostConfirmationHandler but get a disabled "Upgrade to fund" CTA.
// ===========================================================================
function InferenceBalanceCard({ usage }) {
  const queryClient = useQueryClient();
  const { data: inf, isLoading, error: balanceError } = useQuery('inferenceBalance', getInferenceBalance);

  const [fundOpen, setFundOpen]       = useState(false);
  const [autoOpen, setAutoOpen]       = useState(false);

  const inference = usage?.inference;
  const endpoints = inference?.endpoints ?? {};
  const totals    = inference?.totals    ?? {};

  const balanceCents = inf?.balanceCents ?? 0;
  const auto         = inf?.autoRecharge ?? {};
  const canFund      = inf?.canFund ?? false;
  const plan         = inf?.plan ?? 'free';

  return (
    <Paper elevation={0} sx={{ border: '1px solid #E4E4E7', borderRadius: 2, mt: 3 }}>
      {balanceError && (
        <Box sx={{ p: 2.5, pb: 0 }}>
          <ApiErrorAlert error={balanceError} />
        </Box>
      )}
      <Box sx={{ p: 2.5, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <SectionHeader title="Inference balance" tooltip="Pre-paid ledger that absorbs the token-cost portion of inference calls. Separate from your monthly plan allowance, which covers the per-call platform overhead." />
        </Box>
        <Stack direction="row" spacing={1}>
          {canFund
            ? <Button size="small" variant="outlined" onClick={() => setFundOpen(true)}>Add funds</Button>
            : <Tooltip title={plan === 'free' ? 'Upgrade to Starter to fund manually' : 'Set up a payment method to fund manually'}>
                <span><Button size="small" variant="outlined" disabled>Add funds</Button></span>
              </Tooltip>}
          {canFund
            ? <Button size="small" variant="outlined" onClick={() => setAutoOpen(true)}>
                Auto-recharge {auto.enabled ? '(on)' : ''}
              </Button>
            : <Tooltip title="Auto-recharge requires a paid plan and saved payment method">
                <span><Button size="small" variant="outlined" disabled>Auto-recharge</Button></span>
              </Tooltip>}
        </Stack>
      </Box>

      <Box sx={{ px: 2.5, pb: 2 }}>
        {isLoading
          ? <Skeleton width={160} height={40} />
          : <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="h4" fontWeight={700}>
                ${(balanceCents / 100).toFixed(2)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({balanceCents.toLocaleString()}¢ available)
              </Typography>
              {auto.enabled && (
                <Chip
                  label={`Auto-recharge: $${((auto.amountCents ?? 0) / 100).toFixed(2)} when below $${((auto.thresholdCents ?? 0) / 100).toFixed(2)}`}
                  size="small"
                  sx={{ ml: 1, bgcolor: '#ECFDF5', color: '#065F46', fontWeight: 600, fontSize: 11 }}
                />
              )}
            </Box>}
        {auto.enabled && auto.thresholdCents && auto.amountCents && balanceCents < auto.thresholdCents * 1.25 && (
          <Alert severity="info" sx={{ mt: 1.5, fontSize: 12 }}>
            Your balance is close to the auto-recharge threshold. The next inference call may trigger a charge of ${((auto.amountCents) / 100).toFixed(2)}.
          </Alert>
        )}
      </Box>

      {/* Per-endpoint inference activity for the current month */}
      <Divider />
      <Table size="small">
        <TableHead sx={{ bgcolor: 'background.default' }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Endpoint</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Calls</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Input tokens</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Output tokens</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <InferenceRow label="POST /v1/chat"               data={endpoints.chat} loading={isLoading} />
          <InferenceRow label="POST /v1/rag"                data={endpoints.rag}  loading={isLoading} />
          <InferenceRow label="POST /v1/documents/{id}/ask" data={endpoints.ask}  loading={isLoading} />
          <TableRow sx={{ '& td': { fontWeight: 600, bgcolor: '#FAFAFA' } }}>
            <TableCell sx={{ fontSize: 13 }}>Total</TableCell>
            <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>{fmt(totals.calls)}</TableCell>
            <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>{fmt(totals.inputTokens)}</TableCell>
            <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>{fmt(totals.outputTokens)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <FundDialog
        open={fundOpen}
        onClose={() => setFundOpen(false)}
      />
      <AutoRechargeDialog
        open={autoOpen}
        onClose={() => setAutoOpen(false)}
        current={auto}
        onSaved={() => queryClient.invalidateQueries('inferenceBalance')}
      />
    </Paper>
  );
}

function InferenceRow({ label, data, loading }) {
  if (loading) {
    return (
      <TableRow>
        <TableCell><Skeleton /></TableCell>
        <TableCell><Skeleton /></TableCell>
        <TableCell><Skeleton /></TableCell>
        <TableCell><Skeleton /></TableCell>
      </TableRow>
    );
  }
  const d = data ?? {};
  return (
    <TableRow hover>
      <TableCell sx={{ fontSize: 13, fontFamily: 'monospace' }}>{label}</TableCell>
      <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>{fmt(d.calls)}</TableCell>
      <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>{fmt(d.inputTokens)}</TableCell>
      <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>{fmt(d.outputTokens)}</TableCell>
    </TableRow>
  );
}

function FundDialog({ open, onClose }) {
  const [amount, setAmount] = useState('25.00'); // dollars
  const [error, setError]   = useState('');

  const fund = useMutation(
    (amountCents) => fundInferenceBalance(amountCents),
    {
      onSuccess: (data) => { if (data?.url) window.location.href = data.url; },
      onError:   (e)    => { setError(e?.response?.data?.message || 'Failed to start checkout'); },
    }
  );

  const handleFund = () => {
    setError('');
    const dollars = parseFloat(amount);
    if (Number.isNaN(dollars) || dollars < 1 || dollars > 10000) {
      setError('Amount must be between $1 and $10,000');
      return;
    }
    fund.mutate(Math.round(dollars * 100));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add funds to inference balance</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          You'll be redirected to Stripe Checkout to complete payment. The amount is added to your inference balance immediately on success.
        </Typography>
        <TextField
          label="Amount (USD)"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          fullWidth
          autoFocus
          inputProps={{ inputMode: 'decimal' }}
          helperText="Minimum $1.00, maximum $10,000.00"
        />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleFund} variant="contained" disabled={fund.isLoading}>
          {fund.isLoading ? 'Redirecting…' : 'Continue to Stripe'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AutoRechargeDialog({ open, onClose, current, onSaved }) {
  const [enabled, setEnabled]     = useState(false);
  const [amount, setAmount]       = useState('50.00');
  const [threshold, setThreshold] = useState('10.00');
  const [error, setError]         = useState('');

  React.useEffect(() => {
    if (open) {
      setEnabled(Boolean(current?.enabled));
      if (current?.amountCents)    setAmount((current.amountCents / 100).toFixed(2));
      if (current?.thresholdCents) setThreshold((current.thresholdCents / 100).toFixed(2));
      setError('');
    }
  }, [open, current]);

  const save = useMutation(
    (body) => updateInferenceAutoRecharge(body),
    {
      onSuccess: () => { onSaved && onSaved(); onClose(); },
      onError:   (e) => { setError(e?.response?.data?.message || 'Failed to save settings'); },
    }
  );

  const handleSave = () => {
    setError('');
    if (!enabled) { save.mutate({ enabled: false }); return; }
    const amt = parseFloat(amount);
    const thr = parseFloat(threshold);
    if (Number.isNaN(amt) || amt < 10 || amt > 10000) {
      setError('Amount must be between $10 and $10,000'); return;
    }
    if (Number.isNaN(thr) || thr < 0 || thr >= amt) {
      setError('Threshold must be ≥ $0 and < amount (otherwise auto-recharge would loop)'); return;
    }
    save.mutate({
      enabled: true,
      amountCents:    Math.round(amt * 100),
      thresholdCents: Math.round(thr * 100),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Auto-recharge inference balance</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          When your inference balance drops below the threshold, we'll automatically charge your saved payment method for the configured amount.
        </Typography>
        <FormControlLabel
          control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
          label={enabled ? 'Auto-recharge enabled' : 'Auto-recharge disabled'}
        />
        {enabled && (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Recharge amount (USD)"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              fullWidth
              inputProps={{ inputMode: 'decimal' }}
              helperText="How much to add when triggered. Min $10, max $10,000."
            />
            <TextField
              label="Threshold (USD)"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              fullWidth
              inputProps={{ inputMode: 'decimal' }}
              helperText="Trigger when balance drops below this. Must be less than recharge amount."
            />
            {parseFloat(threshold) > 0 && parseFloat(amount) > parseFloat(threshold)
              && parseFloat(threshold) / parseFloat(amount) >= 0.8 && (
              <Alert severity="warning" sx={{ fontSize: 12 }}>
                A threshold of {Math.round(parseFloat(threshold) / parseFloat(amount) * 100)}% of the recharge amount risks frequent charges. Consider lowering the threshold.
              </Alert>
            )}
          </Stack>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={save.isLoading}>
          {save.isLoading ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
